/**
 * Consumer-page registry.
 *
 * Pages listed here attract personal/consumer users (e.g. someone Googling
 * "locked out of my Microsoft account") rather than our B2B ICP. On these
 * pages we:
 *
 *   - suppress all conversion CTAs (ContextualServiceCTA, sticky mobile,
 *     floating help, exit intent, early-mobile contact banner)
 *   - render Google AdSense Auto Ads instead via <AdSense />
 *
 * Keys for KB articles use `{category}/{slug}` to avoid slug collisions
 * across categories. Tools and blog posts use their slug directly.
 */

export const CONSUMER_KB_ARTICLES = new Set<string>([
  // Microsoft account / login / recovery
  'microsoft/how-to-recover-microsoft-account',
  'microsoft/how-to-use-microsoft-account-recovery-form',
  'microsoft/how-to-delete-microsoft-account-permanently',
  'microsoft/how-to-remove-microsoft-account-windows-10',
  'microsoft/how-to-install-windows-11-without-microsoft-account',
  'microsoft/how-to-unlink-microsoft-account-ps5',
  'microsoft/how-to-set-up-microsoft-passkey',
  'microsoft/how-to-transfer-microsoft-authenticator-new-phone',
  'microsoft/how-to-fix-microsoft-authenticator-not-working',
  'microsoft/how-to-use-microsoft-edge-password-manager',

  // Microsoft scam / phishing awareness
  'microsoft/how-to-identify-microsoft-security-alert-scams',
  'microsoft/how-to-verify-microsoft-single-use-code-emails',

  // Microsoft consumer billing / subscriptions / hardware
  'microsoft/how-to-cancel-microsoft-365-subscription',
  'microsoft/how-to-view-microsoft-purchase-history',
  'microsoft/how-to-check-surface-warranty-status',
  'microsoft/how-to-set-up-microsoft-mouse-keyboard-center',

  // Microsoft Windows / app maintenance (consumer searches)
  'microsoft/how-to-update-microsoft-outlook',
  'microsoft/how-to-update-microsoft-teams',
  'microsoft/how-to-uninstall-disable-microsoft-edge',
  'microsoft/how-to-disable-microsoft-compatibility-telemetry',
  'microsoft/how-to-use-microsoft-safety-scanner',

  // macOS personal troubleshooting
  'macos/how-to-erase-reinstall-macos-recovery',
  'macos/how-to-reset-smc-nvram-mac',

  // Generic "save as PDF" — high-volume consumer queries
  'google-workspace/how-to-save-google-doc-as-pdf',
  'microsoft-365/how-to-save-office-365-documents-as-pdf',
]);

export const CONSUMER_BLOG_POSTS = new Set<string>([
  // Bucket-2 "ads" pages — high-traffic posts with no surviving SaaS-funnel fit
  // after the cyber-practice wind-down. Monetize via Auto Ads, not conversion
  // CTAs (2026-05-31 ads strategy; views = trailing 28-day GA4).
  'windows-update-commands-powershell-usoclient-amp-wuauclt', // ~2.9k/mo — sysadmin commands
  'azure-active-directory-microsoft-entra-id-guide',          // ~1.3k/mo — IT identity admin
  'trace-geographic-origin-email',                            // ~0.6k/mo — email forensics
  'biometric-authentication-far-frr-cer-guide',               // ~0.5k/mo — security concept
]);

export const CONSUMER_TOOLS = new Set<string>([
  // Bucket-2 "ads" pages — high-traffic utility/novelty tools with no SaaS-funnel
  // fit. Monetize via Auto Ads, not conversion CTAs (2026-05-31 ads strategy;
  // views = trailing 28-day GA4).
  'ascii-art-generator',      // ~3.2k/mo — novelty generator
  'xor-cipher',               // ~1.8k/mo — CTF / crypto toy
  'substitution-cipher',      // ~0.8k/mo — CTF / crypto toy
  'caesar-cipher',            // ~0.7k/mo — CTF / crypto toy
  'time-duration-calculator', // ~1.0k/mo — generic calculator
]);

export function isConsumerKBArticle(category: string, slug: string): boolean {
  return CONSUMER_KB_ARTICLES.has(`${category}/${slug}`);
}

export function isConsumerBlogPost(slug: string): boolean {
  return CONSUMER_BLOG_POSTS.has(slug);
}

export function isConsumerTool(slug: string): boolean {
  return CONSUMER_TOOLS.has(slug);
}
